import TimeDiff from "components/basic/timer/TimeDiff";
import useCopyDataset from "hooks/useCopyDataset";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import React, { CSSProperties, useState } from "react";
import { Sparklines, SparklinesBars, SparklinesLine } from "react-sparklines";
import { Tooltip } from "primereact/tooltip";
import { formatTime } from "utils/time-utils";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";

interface ISparkLineHandlerProps {
  title: string;
  dataset: string;
  dataArray: number[];
  lastValueTime: Date | null;
  sparklineWidth?: number;
  labelFontSize?: string;
  decimalPlaces?: number;
  showMinMax?: boolean;
  unit?: string;
}

const SparkLineHandler = (props: ISparkLineHandlerProps) => {
  const [dialog, setDialog] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const labelFontSize = props.labelFontSize || "2rem";
  const sparklineWidth = props.sparklineWidth || "300px";
  const decimalPlaces = props.decimalPlaces || 0;

  const style = {
    fontSize: labelFontSize,
    fontWeight: 800,
    whiteSpace: "nowrap",
    lineHeight: "2rem",
  } as CSSProperties;
  const id = `sparkline_value_${(Math.random() + 1).toString(16).substring(8)}`;

  useCopyDataset(isHovered, props.title, props.dataset);

  const handleValueAndUnitTemplate = (value: number | string, unit?: string) => {
    if (!unit) {
      return value;
    }
    return `${value} ${unit}`;
  };

  let el;
  if (props.dataArray && props.dataArray.length !== 0) {
    const valuePre = props.dataArray[props.dataArray.length - 1];
    const value = valuePre.toFixed(decimalPlaces);

    const minValue = Math.min(...props.dataArray).toFixed(decimalPlaces);
    const maxValue = Math.max(...props.dataArray).toFixed(decimalPlaces);

    el = (
      <>
        <div
          style={{
            lineHeight: "2rem",
            width: sparklineWidth,
            height: "40px",
            display: "inline-block",
          }}
        >
          <Sparklines data={props.dataArray} height={30} preserveAspectRatio="none">
            <SparklinesLine style={{ stroke: "#41c3f9", fill: "none" }} />
            <SparklinesBars style={{ fill: "#41c3f9", fillOpacity: ".25" }} />
          </Sparklines>
        </div>
        <div
          id={`${id}`}
          className="ea-sparkline-value"
          style={style}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {handleValueAndUnitTemplate(value, props.unit)}
        </div>

        <Tooltip target={`#${id}`} mouseTrack mouseTrackLeft={10}>
          {props.lastValueTime && (
            <div>
              <b>{T("word.spark-line.last-value-time")}:</b> {formatTime(props.lastValueTime)} (
              {`${T("word.age")}: `}
              <TimeDiff time={props.lastValueTime} />)
            </div>
          )}
          {props.showMinMax && (
            <>
              <div>
                <b>{T("word.min")}:</b> {minValue} {props.unit}
              </div>
              <div>
                <b>{T("word.max")}:</b> {maxValue} {props.unit}
              </div>
            </>
          )}
        </Tooltip>
      </>
    );
  } else {
    el = <h4>{T("word.no-data")}</h4>;
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          paddingBottom: "1rem",
          columnGap: "2rem",
        }}
        onDoubleClick={() => setDialog(true)}
      >
        {el}
      </div>

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => {
          setDialog(false);
        }}
      >
        <DatasetLineChart
          title=""
          identifiers={[props.dataset]}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </>
  );
};

export default SparkLineHandler;
